/**
 * エクスプローラーペイン: API / ローカルフォルダの階層ツリー＋ .keisuke フィルタ
 * 新規・開く・保存・別名保存・計算式・使い方。
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useId, useMemo, useRef, useState, memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Button, Form } from 'react-bootstrap';
import { useAuth } from '../../../../hooks/useAuth';
import {
  listKeisukeFiles,
  getKeisukeFile,
  putKeisukeFile,
  openKeisukeFromDisk,
  saveKeisukeToDisk,
  openFolderFromDisk,
  listDirectoryEntries,
  readKeisukeFromFileHandle,
  type KeisukeFileListItem,
  type DirEntry,
} from '../../../../services/systems/keisuke/keisukeFileStorage';
import { findKeisukeAnalysisLabel } from './keisukeAnalysisPicker';
import {
  getKeisukeFilesystemRoot,
  getKeisukeFilesystemEntries,
  getKeisukeFileContentFromApi,
  type KeisukeFilesystemRoot,
  type KeisukeFilesystemEntry,
} from '../../../../services/systems/keisuke/keisukeFilesystemApi';
import {
  KEISUKE_DEFAULT_TTEST_N_PER_GROUP,
  normalizeAnalysisType,
  serializeDashboardState,
  useKeisukeDashboardStore,
  validateKeisukeFile,
  type AnalysisTypeKey,
} from '../../../../stores/systems/keisuke/dashboardStore';
import { KEISUKE_DASHBOARD_NEW_EVENT } from './KeisukeDashboardBarChartSettingsContext';
import styles from './KeisukeDashboardExplorerPane.module.scss';

type ExplorerFileFilter = 'keisuke-only' | 'all';

/** コンパクト左ドック用: アイコン＋短いキャプションの同一ボタン */
const ExplorerDockButton = memo(function ExplorerDockButton({
  icon,
  caption,
  title,
  ariaLabel,
  onClick,
  to,
  compact,
}: {
  icon: string;
  caption?: string;
  title: string;
  ariaLabel: string;
  onClick?: () => void;
  to?: string;
  compact: boolean;
}) {
  const className = `${styles.toolbarBtn} ${styles.toolbarIconBtn}`;
  const body = (
    <>
      <i className={`fas ${icon}`} aria-hidden />
      {compact && caption ? <span className={styles.toolbarBtnCaption}>{caption}</span> : null}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className={`btn btn-sm btn-outline-secondary ${className}`}
        title={title}
        aria-label={ariaLabel}
      >
        {body}
      </Link>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      className={className}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
    >
      {body}
    </Button>
  );
});

export default function KeisukeDashboardExplorerPane({
  compact = false,
}: {
  /** ゲスト空状態用: ツールバーだけ表示 */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const explorerOpenFileInputId = useId();
  const explorerFileFilterSelectId = useId();
  const [fileList, setFileList] = useState<KeisukeFileListItem[]>([]);
  const [fileFilter, setFileFilter] = useState<ExplorerFileFilter>('keisuke-only');
  const fileHandleRef = useRef<FileSystemFileHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasFilePicker = typeof window !== 'undefined' && 'showOpenFilePicker' in window;
  const hasDirectoryPicker = typeof window !== 'undefined' && 'showDirectoryPicker' in window;

  const [openedFolderHandle, setOpenedFolderHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [openedFolderName, setOpenedFolderName] = useState<string>('');
  const [folderEntriesCache, setFolderEntriesCache] = useState<Map<string, DirEntry[]>>(() => new Map());
  const [folderExpandedPaths, setFolderExpandedPaths] = useState<Set<string>>(() => new Set());
  /** 現在開いているフォルダのパス（このフォルダと親を開いたアイコンで表示） */
  const [currentFolderPath, setCurrentFolderPath] = useState<string>('');

  /** バックエンドAPI: ユーザー別フォルダ起点の階層 */
  const [apiRoot, setApiRoot] = useState<KeisukeFilesystemRoot | null>(null);
  /** ルート取得が一度完了したか（失敗・未存在含む。remount 振動防止） */
  const [apiRootResolved, setApiRootResolved] = useState(false);
  const [apiEntriesCache, setApiEntriesCache] = useState<Map<string, KeisukeFilesystemEntry[]>>(() => new Map());
  const [apiExpandedPaths, setApiExpandedPaths] = useState<Set<string>>(() => new Set(['']));

  const { hydrateFromSerialized, setCurrentFile, currentFileId, currentFileName, analysisType } =
    useKeisukeDashboardStore(
      useShallow((s) => ({
        hydrateFromSerialized: s.hydrateFromSerialized,
        setCurrentFile: s.setCurrentFile,
        currentFileId: s.currentFileId,
        currentFileName: s.currentFileName,
        analysisType: s.analysisOptions.type,
      }))
    );

  const apiRootEntryCount = apiEntriesCache.get('')?.length ?? 0;
  const userId = user?.id ?? null;

  useEffect(() => {
    // remount 直後の空状態で false を飛ばすと compact↔full が振動するため、API 未取得なら縮小通知しない
    const hasContent =
      Boolean(openedFolderHandle) || fileList.length > 0 || apiRootEntryCount > 0;
    if (!hasContent && userId != null && !apiRootResolved) return;
    window.dispatchEvent(
      new CustomEvent('keisuke-explorer-needs-room', { detail: { needsRoom: hasContent } })
    );
  }, [apiRootResolved, apiRootEntryCount, openedFolderHandle, fileList.length, userId]);

  /** 現在開いているファイルの保存日時（IndexedDB 由来のときのみ） */
  const currentFileSavedAt = useMemo(() => {
    if (!currentFileId || currentFileId.startsWith('api:')) return null;
    const item = fileList.find((f) => f.id === currentFileId);
    return item?.savedAt ?? null;
  }, [currentFileId, fileList]);

  const filterDirEntries = useCallback(
    (entries: DirEntry[]): DirEntry[] => {
      if (fileFilter === 'all') return entries;
      return entries.filter(
        (e) => e.kind === 'directory' || (e.kind === 'file' && e.name.toLowerCase().endsWith('.keisuke'))
      );
    },
    [fileFilter]
  );

  /** 開いたフォルダの直下のエントリ（フィルタ済み）。階層表示に使用 */
  const rootFolderEntries = useMemo(
    () => filterDirEntries(folderEntriesCache.get('') ?? []),
    [folderEntriesCache, filterDirEntries]
  );

  const getFolderHandleForPath = useCallback(
    (path: string): FileSystemDirectoryHandle | null => {
      if (!openedFolderHandle) return null;
      if (path === '') return openedFolderHandle;
      const segments = path.split('/').filter(Boolean);
      let currentPath = '';
      let currentHandle: FileSystemDirectoryHandle = openedFolderHandle;
      for (const seg of segments) {
        const entries = folderEntriesCache.get(currentPath);
        if (!entries) return null;
        const entry = entries.find((e) => e.name === seg && e.kind === 'directory');
        if (!entry) return null;
        currentHandle = entry.handle as FileSystemDirectoryHandle;
        currentPath = currentPath ? `${currentPath}/${seg}` : seg;
      }
      return currentHandle;
    },
    [openedFolderHandle, folderEntriesCache]
  );

  const loadFolderChildren = useCallback(
    async (path: string) => {
      const handle = path === '' ? openedFolderHandle : getFolderHandleForPath(path);
      if (!handle) return;
      const entries = await listDirectoryEntries(handle);
      setFolderEntriesCache((prev) => {
        const next = new Map(prev);
        next.set(path, entries);
        return next;
      });
    },
    [openedFolderHandle, getFolderHandleForPath]
  );

  const toggleFolderExpanded = useCallback(
    async (path: string) => {
      if (!folderEntriesCache.has(path)) {
        await loadFolderChildren(path);
      }
      setFolderExpandedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
          setCurrentFolderPath(path);
        }
        return next;
      });
    },
    [folderEntriesCache, loadFolderChildren]
  );


  /** 現在開いているフォルダまたはその親なら開いたアイコン（📂）を使用 */
  const isFolderOpenIcon = useCallback(
    (path: string) =>
      path === currentFolderPath ||
      (currentFolderPath !== '' && currentFolderPath.startsWith(path + '/')) ||
      (path === '' && currentFolderPath !== ''),
    [currentFolderPath]
  );

  const handleOpenFolder = useCallback(async () => {
    if (!hasDirectoryPicker) return;
    try {
      const handle = await openFolderFromDisk();
      if (!handle) return;
      const entries = await listDirectoryEntries(handle);
      setOpenedFolderHandle(handle);
      setOpenedFolderName(handle.name);
      setFolderEntriesCache(new Map([['', entries]]));
      setFolderExpandedPaths(new Set(['']));
      setCurrentFolderPath('');
    } catch (e) {
      alert(t('keisuke.dashboard.explorer.errorOpen') + (e instanceof Error ? ` ${e.message}` : ''));
    }
  }, [hasDirectoryPicker, t]);

  const handleOpenFolderFile = useCallback(
    async (handle: FileSystemFileHandle) => {
      try {
        const result = await readKeisukeFromFileHandle(handle);
        if (!result) {
          alert(t('keisuke.dashboard.errorInvalidKeisuke'));
          return;
        }
        fileHandleRef.current = handle;
        hydrateFromSerialized(result.data);
        setCurrentFile(null, result.fileName);
      } catch (e) {
        alert(t('keisuke.dashboard.explorer.errorOpen') + (e instanceof Error ? ` ${e.message}` : ''));
      }
    },
    [hydrateFromSerialized, setCurrentFile, t]
  );

  const refreshList = useCallback(async () => {
    const list = await listKeisukeFiles();
    setFileList(list);
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  /** バックエンドAPI: ルート取得とルート直下のエントリを読み込み（ログイン時のみ・連打防止） */
  useEffect(() => {
    if (userId == null) {
      setApiRootResolved(false);
      return;
    }
    let cancelled = false;
    setApiRootResolved(false);
    void (async () => {
      const root = await getKeisukeFilesystemRoot();
      if (cancelled) return;
      if (root?.exists) {
        setApiRoot(root);
        const entries = await getKeisukeFilesystemEntries('');
        if (!cancelled) {
          setApiEntriesCache((prev) => new Map(prev).set('', entries));
        }
      } else {
        setApiRoot(null);
      }
      if (!cancelled) setApiRootResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const toggleApiExpanded = useCallback(async (path: string) => {
    setApiExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    setCurrentFolderPath(path);
    const entries = await getKeisukeFilesystemEntries(path);
    setApiEntriesCache((prev) => new Map(prev).set(path, entries));
  }, []);

  const filterApiEntries = useCallback((entries: KeisukeFilesystemEntry[]) => {
    if (fileFilter === 'all') return entries;
    return entries.filter(
      (e) => e.isDirectory || (e.name.toLowerCase().endsWith('.keisuke'))
    );
  }, [fileFilter]);

  const handleNew = useCallback(() => {
    const type = normalizeAnalysisType(analysisType);
    setCurrentFile(null, null);
    fileHandleRef.current = null;
    hydrateFromSerialized({
      gridData: [],
      gridHeaders: [],
      analysisOptions: { type },
      activeResultTab:
        type === 't-test' || type === 'anova' || type === 'regression' ? 'comparison' : 'stats',
      resultCache: null,
      rightPaneResultRatio: 0.35,
      tTestNPerGroup: KEISUKE_DEFAULT_TTEST_N_PER_GROUP,
    });
    window.dispatchEvent(new CustomEvent(KEISUKE_DASHBOARD_NEW_EVENT));
  }, [setCurrentFile, hydrateFromSerialized, analysisType]);

  const handleOpenFromDisk = useCallback(async () => {
    if (!hasFilePicker) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const result = await openKeisukeFromDisk();
      if (!result) return;
      fileHandleRef.current = result.handle ?? null;
      hydrateFromSerialized(result.data);
      setCurrentFile(result.id, result.fileName);
    } catch (e) {
      alert(t('keisuke.dashboard.explorer.errorOpen') + (e instanceof Error ? ` ${e.message}` : ''));
    }
  }, [hasFilePicker, hydrateFromSerialized, setCurrentFile, t]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const raw = JSON.parse(String(reader.result ?? ''));
          const result = validateKeisukeFile(raw);
          if (!result.ok) {
            alert(t('keisuke.dashboard.errorInvalidKeisuke'));
            return;
          }
          const id = crypto.randomUUID?.() ?? `keisuke-${Date.now()}`;
          const data = result.data;
          await putKeisukeFile({
            id,
            fileName: file.name,
            analysisType: (data.analysisOptions?.type as AnalysisTypeKey) ?? 't-test',
            savedAt: data.savedAt ?? new Date().toISOString(),
            content: data,
          });
          hydrateFromSerialized(data);
          setCurrentFile(id, file.name);
          refreshList();
        } catch {
          alert(t('keisuke.dashboard.errorInvalidKeisuke'));
        }
      };
      reader.readAsText(file, 'UTF-8');
    },
    [hydrateFromSerialized, setCurrentFile, refreshList, t]
  );

  const handleSave = useCallback(async () => {
    const state = useKeisukeDashboardStore.getState();
    const content = serializeDashboardState(state);
    const fileName = state.currentFileName || `keisuke_${new Date().toISOString().slice(0, 10)}.keisuke`;
    try {
      const result = await saveKeisukeToDisk(content, fileName, fileHandleRef.current);
      if (!result) return;
      fileHandleRef.current = result.handle ?? null;
      if (state.currentFileId) {
        const record = await getKeisukeFile(state.currentFileId);
        if (record) {
          record.content = content;
          record.fileName = result.fileName;
          record.savedAt = new Date().toISOString();
          await putKeisukeFile(record);
        }
        setCurrentFile(state.currentFileId, result.fileName);
      } else {
        const id = crypto.randomUUID?.() ?? `keisuke-${Date.now()}`;
        await putKeisukeFile({
          id,
          fileName: result.fileName,
          analysisType: (content.analysisOptions?.type as AnalysisTypeKey) ?? 't-test',
          savedAt: content.savedAt ?? new Date().toISOString(),
          content,
        });
        setCurrentFile(id, result.fileName);
        setFileList((prev) => [{ id, fileName: result.fileName, analysisType: (content.analysisOptions?.type as AnalysisTypeKey) ?? 't-test', savedAt: content.savedAt ?? '' }, ...prev]);
      }
      refreshList();
    } catch (e) {
      alert(t('keisuke.dashboard.explorer.errorSave') + (e instanceof Error ? ` ${e.message}` : ''));
    }
  }, [setCurrentFile, refreshList, t]);

  const handleSaveAs = useCallback(async () => {
    const state = useKeisukeDashboardStore.getState();
    const content = serializeDashboardState(state);
    const suggested = state.currentFileName || `keisuke_${new Date().toISOString().slice(0, 10)}.keisuke`;
    try {
      const result = await saveKeisukeToDisk(content, suggested, null);
      if (!result) return;
      fileHandleRef.current = result.handle ?? null;
      const id = crypto.randomUUID?.() ?? `keisuke-${Date.now()}`;
      await putKeisukeFile({
        id,
        fileName: result.fileName,
        analysisType: (content.analysisOptions?.type as AnalysisTypeKey) ?? 't-test',
        savedAt: content.savedAt ?? new Date().toISOString(),
        content,
      });
      setCurrentFile(id, result.fileName);
      refreshList();
    } catch (e) {
      alert(t('keisuke.dashboard.explorer.errorSave') + (e instanceof Error ? ` ${e.message}` : ''));
    }
  }, [setCurrentFile, refreshList, t]);

  /** API経由で .keisuke を開く（バックエンドのファイルシステムから取得） */
  const handleOpenApiFile = useCallback(
    async (relativePath: string) => {
      const raw = await getKeisukeFileContentFromApi(relativePath);
      if (raw == null) {
        alert(t('keisuke.dashboard.explorer.errorOpen'));
        return;
      }
      const result = validateKeisukeFile(raw);
      if (!result.ok) {
        alert(t('keisuke.dashboard.errorInvalidKeisuke'));
        return;
      }
      fileHandleRef.current = null;
      hydrateFromSerialized(result.data);
      const fileId = `api:${relativePath}`;
      const fileName = relativePath.split('/').pop() ?? relativePath;
      setCurrentFile(fileId, fileName);
    },
    [hydrateFromSerialized, setCurrentFile, t]
  );

  /** バックエンドAPIのエントリでツリーを描画（ユーザー別データルート） */
  function renderApiTree(relativePath: string, depth: number): React.ReactNode {
    const entries = filterApiEntries(apiEntriesCache.get(relativePath) ?? []);
    if (entries.length === 0) return null;
    const isRoot = relativePath === '';
    const pad = depth <= 1 ? 24 : 24 + (depth - 1) * 16;
    return (
      <ul className={styles.treeFolder}>
        {entries.map((entry) => {
          const childPath = isRoot ? entry.name : `${relativePath}/${entry.name}`;
          if (entry.isDirectory) {
            const isExpanded = apiExpandedPaths.has(childPath);
            const useOpenIcon = currentFolderPath === childPath || childPath.startsWith(currentFolderPath + '/');
            return (
              <li key={childPath} className={styles.treeFolder}>
                <div
                  className={styles.treeRow}
                  style={{ paddingLeft: pad }}
                  onClick={() => toggleApiExpanded(childPath)}
                >
                  <span className={styles.treeToggle}>{isExpanded ? '−' : '+'}</span>
                  <span className={styles.treeIcon}>{useOpenIcon ? '📂' : '📁'}</span>
                  <span className={styles.treeLabel} title={entry.name}>
                    {entry.name}
                  </span>
                </div>
                {isExpanded && renderApiTree(childPath, depth + 1)}
              </li>
            );
          }
          const isKeisuke = entry.name.toLowerCase().endsWith('.keisuke');
          return (
            <li key={childPath}>
              <div
                className={`${styles.treeRow} ${currentFileId === `api:${childPath}` ? styles.active : ''}`}
                style={{ paddingLeft: pad }}
                onClick={() => isKeisuke && handleOpenApiFile(childPath)}
                role={isKeisuke ? 'button' : undefined}
              >
                <span className={styles.treeToggle} />
                <span className={styles.treeBullet}>●</span>
                <span className={styles.treeLabel} title={entry.name}>
                  {entry.name}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  function renderFolderTree(parentPath: string, depth: number): React.ReactNode {
    const entries = filterDirEntries(folderEntriesCache.get(parentPath) ?? []);
    if (entries.length === 0) return null;
    return (
      <ul className={styles.treeFolder}>
        {entries.map((entry) => {
          const path = parentPath ? `${parentPath}/${entry.name}` : entry.name;
          if (entry.kind === 'directory') {
            const isExpanded = folderExpandedPaths.has(path);
            const useOpenIcon = isFolderOpenIcon(path);
            return (
              <li key={path} className={styles.treeFolder}>
                <div
                  className={styles.treeRow}
                  style={{ paddingLeft: 4 + depth * 16 }}
                  onClick={() => toggleFolderExpanded(path)}
                >
                  <span className={styles.treeToggle}>{isExpanded ? '−' : '+'}</span>
                  <span className={styles.treeIcon}>{useOpenIcon ? '📂' : '📁'}</span>
                  <span className={styles.treeLabel} title={entry.name}>
                    {entry.name}
                  </span>
                </div>
                {isExpanded && renderFolderTree(path, depth + 1)}
              </li>
            );
          }
          const isKeisuke = entry.name.toLowerCase().endsWith('.keisuke');
          return (
            <li key={path}>
              <div
                className={styles.treeRow}
                style={{ paddingLeft: 4 + depth * 16 }}
                onClick={() => isKeisuke && handleOpenFolderFile(entry.handle)}
                role={isKeisuke ? 'button' : undefined}
              >
                <span className={styles.treeToggle} />
                <span className={styles.treeBullet}>●</span>
                <span className={styles.treeLabel} title={entry.name}>
                  {entry.name}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
    <div className={`${styles.wrap}${compact ? ` ${styles.wrapCompact}` : ''}`}>
      <input
        ref={fileInputRef}
        id={explorerOpenFileInputId}
        name="keisuke-explorer-open-keisuke-file"
        type="file"
        accept=".keisuke,application/json"
        style={{ display: 'none' }}
        tabIndex={-1}
        aria-hidden
        onChange={handleFileInputChange}
      />
      <div className={`${styles.toolbar}${compact ? ` ${styles.toolbarCompactLabeled}` : ''}`}>
        <ExplorerDockButton
          compact={compact}
          icon="fa-file"
          caption={t('keisuke.dashboard.explorer.new')}
          title={t('keisuke.dashboard.tooltips.explorerNew')}
          ariaLabel={t('keisuke.dashboard.explorer.new')}
          onClick={handleNew}
        />
        <ExplorerDockButton
          compact={compact}
          icon="fa-folder-open"
          caption={t('keisuke.dashboard.explorer.open')}
          title={t('keisuke.dashboard.tooltips.explorerOpen')}
          ariaLabel={t('keisuke.dashboard.explorer.open')}
          onClick={handleOpenFromDisk}
        />
        {hasDirectoryPicker && !apiRoot && !compact && (
          <ExplorerDockButton
            compact={false}
            icon="fa-folder-plus"
            title={t('keisuke.dashboard.tooltips.explorerOpenFolder')}
            ariaLabel={t('keisuke.dashboard.explorer.openFolder')}
            onClick={handleOpenFolder}
          />
        )}
        <ExplorerDockButton
          compact={compact}
          icon="fa-save"
          caption={
            currentFileName
              ? t('keisuke.dashboard.explorer.save')
              : t('keisuke.dashboard.toolbar.save')
          }
          title={t('keisuke.dashboard.tooltips.explorerSave')}
          ariaLabel={
            currentFileName
              ? t('keisuke.dashboard.explorer.save')
              : t('keisuke.dashboard.toolbar.save')
          }
          onClick={handleSave}
        />
        <ExplorerDockButton
          compact={compact}
          icon="fa-copy"
          caption={t('keisuke.dashboard.explorer.saveAs')}
          title={t('keisuke.dashboard.tooltips.explorerSaveAs')}
          ariaLabel={t('keisuke.dashboard.explorer.saveAs')}
          onClick={handleSaveAs}
        />
        <ExplorerDockButton
          compact={compact}
          icon="fa-book"
          caption={t('keisuke.workspace.formulas')}
          title={t('keisuke.dashboard.tooltips.explorerFormulas')}
          ariaLabel={t('keisuke.workspace.formulas')}
          to="/keisuke/formulas"
        />
        <ExplorerDockButton
          compact={compact}
          icon="fa-question-circle"
          caption={t('keisuke.workspace.help')}
          title={t('keisuke.workspace.showOnboarding')}
          ariaLabel={t('keisuke.workspace.help')}
          onClick={() => window.dispatchEvent(new CustomEvent('keisuke-show-onboarding'))}
        />
      </div>
      {compact ? (
        <button
          type="button"
          className={styles.compactIdleHint}
          onClick={handleOpenFromDisk}
        >
          <i className={`fas fa-folder-open ${styles.compactIdleHintIcon}`} aria-hidden />
          <span className={styles.compactIdleHintTitle}>
            {t('keisuke.dashboard.explorer.compactIdleHintTitle')}
          </span>
          <span className={styles.compactIdleHintBody}>
            {t('keisuke.dashboard.explorer.compactIdleHint')}
          </span>
          <span className={styles.compactIdleHintCta}>
            {t('keisuke.dashboard.explorer.compactIdleHintCta')}
          </span>
        </button>
      ) : null}
      {!compact && (
      <>
      <div className={styles.filterBar}>
        <label className={styles.filterLabel} htmlFor={explorerFileFilterSelectId}>
          {t('keisuke.dashboard.explorer.show')}
        </label>
        <Form.Select
          id={explorerFileFilterSelectId}
          name="keisuke-explorer-file-filter"
          size="sm"
          className={styles.filterSelect}
          value={fileFilter}
          onChange={(e) => setFileFilter(e.target.value as ExplorerFileFilter)}
          aria-label={t('keisuke.dashboard.explorer.filterAria')}
        >
          <option value="keisuke-only">
            {t('keisuke.dashboard.explorer.filterKeisukeOnly')}
          </option>
          <option value="all">
            {t('keisuke.dashboard.explorer.filterAll')}
          </option>
        </Form.Select>
      </div>
      <div className={styles.listWrap}>
        <ul className={styles.treeRoot}>
          <li className={styles.treeFolder}>
            <div className={styles.treeRowRoot} style={{ paddingLeft: 4 }}>
              <span className={styles.treeToggle}>−</span>
              <span className={styles.treeIcon}>📁</span>
              <span className={styles.treeLabel}>
                {t('keisuke.dashboard.explorer.userDataRoot')}
              </span>
            </div>
            <ul className={styles.treeFolder}>
              {/* バックエンドAPI: uploads/keisuke-users/{userId}/ の階層 */}
              {apiRoot ? (
                <>
                  {!apiEntriesCache.has('') ? (
                    <li>
                      <div className={styles.treeRowExplainer} style={{ paddingLeft: 24 }}>
                        {t('keisuke.dashboard.explorer.loading')}
                      </div>
                    </li>
                  ) : apiEntriesCache.get('')?.length === 0 ? (
                    <li>
                      <div className={styles.treeRowExplainer} style={{ paddingLeft: 24 }}>
                        {t('keisuke.dashboard.explorer.emptyFolder')}
                      </div>
                    </li>
                  ) : (
                    renderApiTree('', 1)
                  )}
                </>
              ) : openedFolderHandle ? (
                <li className={styles.folderSection}>
                  <div
                    className={styles.treeRow}
                    style={{ paddingLeft: 24 }}
                    onClick={() => toggleFolderExpanded('')}
                  >
                    <span className={styles.treeToggle}>
                      {folderExpandedPaths.has('') ? '−' : '+'}
                    </span>
                    <span className={styles.treeIcon}>{isFolderOpenIcon('') ? '📂' : '📁'}</span>
                    <span className={styles.treeLabel} title={openedFolderName}>
                      {openedFolderName}
                    </span>
                  </div>
                  {folderExpandedPaths.has('') && (
                    rootFolderEntries.length === 0 ? (
                      <div className={styles.treeRowExplainer} style={{ paddingLeft: 36 }}>
                        {t('keisuke.dashboard.explorer.emptyFolder')}
                      </div>
                    ) : (
                      renderFolderTree('', 2)
                    )
                  )}
                </li>
              ) : (
                <li>
                  <div className={styles.treeRowExplainer} style={{ paddingLeft: 24 }}>
                    {t(
                      user
                        ? 'keisuke.dashboard.explorer.openFolderHint'
                        : 'keisuke.dashboard.explorer.openFolderHintGuest'
                    )}
                  </div>
                </li>
              )}
            </ul>
          </li>
        </ul>
      </div>
      <div className={styles.detailArea}>
        <div className={styles.detailTitle}>
          {t('keisuke.dashboard.explorer.fileDetails')}
        </div>
        {currentFileName ? (
          <>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t('keisuke.dashboard.explorer.detailName')}
              </span>
              <span title={currentFileName}>{currentFileName}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t('keisuke.dashboard.explorer.detailAnalysis')}
              </span>
              <span>{findKeisukeAnalysisLabel(analysisType as AnalysisTypeKey, t)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t('keisuke.dashboard.explorer.detailSavedAt')}
              </span>
              <span>
                {currentFileSavedAt ? new Date(currentFileSavedAt).toLocaleString() : '—'}
              </span>
            </div>
          </>
        ) : (
          <div className={styles.detailRow}>
            {t('keisuke.dashboard.explorer.detailNoFile')}
          </div>
        )}
      </div>
      </>
      )}
    </div>
    </>
  );
}
